(() => {
  const SECTION_SELECTOR = '.pasquini-hero-slider';

  const isFrameVisible = (video) => {
    const frame = video.closest('.pasquini-hero-slider__media-frame');
    if (!frame) return true;
    return window.getComputedStyle(frame).display !== 'none';
  };

  const playVideo = (video) => {
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    const tryPlay = () => {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    };

    if (video.readyState >= 2) {
      tryPlay();
      return;
    }

    const onReady = () => {
      video.removeEventListener('loadeddata', onReady);
      video.removeEventListener('canplay', onReady);
      tryPlay();
    };

    video.addEventListener('loadeddata', onReady);
    video.addEventListener('canplay', onReady);

    if (video.readyState === 0) {
      video.preload = 'auto';
      try {
        video.load();
      } catch (error) {
        // Ignore load errors from browsers that block reload mid-playback.
      }
    }
  };

  const pauseVideo = (video, { reset = true } = {}) => {
    video.pause();
    if (!reset) return;
    try {
      video.currentTime = 0;
    } catch (error) {
      // Some browsers throw if metadata is not ready yet.
    }
  };

  const getActiveVideos = (swiper) => {
    const activeSlide = swiper.slides[swiper.activeIndex];
    if (!activeSlide) return [];

    return Array.from(activeSlide.querySelectorAll('video')).filter(isFrameVisible);
  };

  const syncVideos = (swiper, { mediaPaused = false } = {}) => {
    const activeSlide = swiper.slides[swiper.activeIndex];

    swiper.el.querySelectorAll('video').forEach((video) => {
      const slide = video.closest('.swiper-slide');
      const isActive = slide === activeSlide && isFrameVisible(video);

      if (isActive && !mediaPaused) {
        playVideo(video);
      } else {
        pauseVideo(video, { reset: !isActive });
      }
    });
  };

  const setToggleState = (button, isPlaying) => {
    button.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
    button.setAttribute(
      'aria-label',
      isPlaying
        ? button.dataset.pauseLabel || 'Pausar slideshow'
        : button.dataset.playLabel || 'Reproduzir slideshow'
    );
  };

  const initPasquiniHeroSlider = (root) => {
    if (!root || root.dataset.swiperReady === 'true') return;
    if (typeof window.Swiper === 'undefined') return;

    const swiperEl = root.querySelector('.pasquini-hero-slider__swiper');
    if (!swiperEl) return;

    const slides = swiperEl.querySelectorAll('.swiper-slide');
    if (slides.length === 0) return;

    const enableLoop = root.dataset.loop === 'true' && slides.length > 1;
    const enableAutoplay = root.dataset.autoplay === 'true' && slides.length > 1;
    const autoplayDelay = Number(root.dataset.autoplayDelay || 5000);
    const paginationEl = root.querySelector('.pasquini-hero-slider__pagination');
    const autoplayToggle = root.querySelector('.pasquini-hero-slider__autoplay');
    const prevButtons = root.querySelectorAll('.pasquini-hero-slider__arrow--prev');
    const nextButtons = root.querySelectorAll('.pasquini-hero-slider__arrow--next');

    let mediaPaused = false;

    const autoplayConfig = enableAutoplay
      ? {
          enabled: true,
          delay: autoplayDelay,
          disableOnInteraction: false,
          pauseOnMouseEnter: false,
        }
      : false;

    const swiper = new window.Swiper(swiperEl, {
      slidesPerView: 1,
      speed: 700,
      loop: enableLoop,
      watchOverflow: true,
      pagination:
        paginationEl && slides.length > 1
          ? {
              el: paginationEl,
              clickable: true,
            }
          : undefined,
      autoplay: autoplayConfig,
      on: {
        init(instance) {
          syncVideos(instance, { mediaPaused });
        },
        slideChange(instance) {
          syncVideos(instance, { mediaPaused });
        },
        slideChangeTransitionEnd(instance) {
          syncVideos(instance, { mediaPaused });
        },
      },
    });

    prevButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        swiper.slidePrev();
      });
    });

    nextButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        swiper.slideNext();
      });
    });

    if (autoplayToggle) {
      // Videos start with autoplay, so the control begins in the "pause" state.
      setToggleState(autoplayToggle, true);

      autoplayToggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const isPlaying = autoplayToggle.getAttribute('aria-pressed') === 'true';

        if (isPlaying) {
          mediaPaused = true;

          if (swiper.autoplay && typeof swiper.autoplay.stop === 'function') {
            swiper.autoplay.stop();
          }

          getActiveVideos(swiper).forEach((video) => pauseVideo(video, { reset: false }));
          setToggleState(autoplayToggle, false);
          return;
        }

        mediaPaused = false;

        if (slides.length > 1) {
          if (!swiper.params.autoplay || swiper.params.autoplay === false) {
            swiper.params.autoplay = {
              enabled: true,
              delay: autoplayDelay,
              disableOnInteraction: false,
              pauseOnMouseEnter: false,
            };
          } else if (typeof swiper.params.autoplay === 'object') {
            swiper.params.autoplay.enabled = true;
            swiper.params.autoplay.delay = autoplayDelay;
          }

          if (swiper.autoplay && typeof swiper.autoplay.start === 'function') {
            swiper.autoplay.start();
          }
        }

        getActiveVideos(swiper).forEach((video) => playVideo(video));
        setToggleState(autoplayToggle, true);
      });
    }

    root.dataset.swiperReady = 'true';
    root._pasquiniHeroSliderSwiper = swiper;
  };

  const initAll = () => {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initPasquiniHeroSlider);
  };

  const waitForSwiper = (attempt = 0) => {
    if (typeof window.Swiper !== 'undefined') {
      initAll();
      return;
    }

    if (attempt > 40) return;
    window.setTimeout(() => waitForSwiper(attempt + 1), 50);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => waitForSwiper());
  } else {
    waitForSwiper();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const sectionRoot =
      event.target.querySelector?.(SECTION_SELECTOR) || event.target.closest?.(SECTION_SELECTOR);
    if (sectionRoot) {
      sectionRoot.dataset.swiperReady = 'false';
      waitForSwiper();
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const sectionRoot =
      event.target.querySelector?.(SECTION_SELECTOR) || event.target.closest?.(SECTION_SELECTOR);
    if (sectionRoot?._pasquiniHeroSliderSwiper) {
      sectionRoot._pasquiniHeroSliderSwiper.destroy(true, true);
      delete sectionRoot._pasquiniHeroSliderSwiper;
      sectionRoot.dataset.swiperReady = 'false';
    }
  });
})();
